import { useEffect, useState, useRef } from "react"; // Import useState, useRef
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import PrototypeBadge from "@/components/ui/PrototypeBadge";
import ScrollToTop from "@/components/utils/ScrollToTop";
import CookieConsentBanner from "@/components/shared/CookieConsentBanner"; // Import the banner
import PublicLayout from "./layouts/public/PublicLayout"; // Import the layout

// Public pages
import Index from "./pages/public/Index";
import OmOss from "./pages/public/OmOss";
import Kontakt from "./pages/public/Kontakt";
import NotFound from "./pages/public/NotFound";
import Community from "./pages/public/Community";
import Kampanjer from "./pages/public/Kampanjer";
import FreddyCyklar from "./pages/public/FreddyCyklar";
import BliSponsor from "@/pages/public/BliSponsor";
import Integritetspolicy from "./pages/public/Integritetspolicy"; 
import CommunityRiktlinjer from "./pages/public/CommunityRiktlinjer";
import CookiePolicy from "./pages/public/CookiePolicy";
import FormConfirmation from "./pages/public/FormConfirmation";
import Berattelser from "./pages/public/Berattelser"; // Import Berattelser page from public folder
import TidigaTeckenPaDemens from "./pages/public/berattelser/TidigaTeckenPaDemens";
import KommuniceraMedPersonerMedDemens from "./pages/public/berattelser/KommuniceraMedPersonerMedDemens";
import SjuFaserAvDemensOchAlzheimer from "./pages/public/berattelser/SjuFaserAvDemensOchAlzheimer"; // Added import
import EttDemensvanligtBoende from "./pages/public/berattelser/EttDemensvanligtBoende"; // Added import
import OmDemens from "./pages/public/OmDemens"; // Import the OmDemens page

const COOKIE_CONSENT_KEY = 'cookie_consent_status';
const GA_MEASUREMENT_ID = "G-DVJBZQCFTP"; // Use constant for ID

// Declare gtag and dataLayer on the window object for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[]; // Add dataLayer declaration
  }
}

const queryClient = new QueryClient();

// Removed RouteDebugger component

const App = () => {
  const location = useLocation();
  const [gaInitialized, setGaInitialized] = useState(false);
  const gaScriptLoaded = useRef(false); // To prevent loading script multiple times

  // Function to load GA script and initialize gtag
  const loadAndInitializeGA = () => {
    const consentStatus = localStorage.getItem(COOKIE_CONSENT_KEY);

    if (consentStatus === 'accepted_all' && !gaScriptLoaded.current) {
      gaScriptLoaded.current = true; // Mark as loading/loaded

      // Check if script already exists (e.g., from previous load attempt)
      if (document.getElementById('ga-gtag-script')) {
        // If script tag exists, maybe it loaded, try initializing state
        if (typeof window.gtag === 'function') {
           setGaInitialized(true);
        }
        return; 
      }

      console.log("Loading Google Analytics script...");
      const script = document.createElement('script');
      script.id = 'ga-gtag-script'; // Add ID to check existence
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      
      script.onload = () => {
        console.log("GA script loaded.");
        // Initialize dataLayer and gtag function if not already present
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function() { window.dataLayer.push(arguments); };
        window.gtag('js', new Date()); // Signal script readiness
        setGaInitialized(true); // Set state to trigger GA logic useEffect
      };
      script.onerror = () => {
        console.error("Failed to load Google Analytics script.");
        gaScriptLoaded.current = false; // Allow retry if needed? Or handle error state
      };
      document.head.appendChild(script);

    } else if (consentStatus === 'accepted_all' && gaScriptLoaded.current && typeof window.gtag === 'function') {
       // If consent exists and script is marked loaded, ensure GA state is initialized
       // This handles cases where component re-mounts after script load but before state update
       setGaInitialized(true);
    } else {
      console.log("GA consent not given or status is not 'accepted_all'. GA not loaded/initialized.");
      // Ensure GA is marked as not initialized if consent is withdrawn or only necessary accepted
      setGaInitialized(false); 
    }
  };

  // Check consent on initial mount
  useEffect(() => {
    loadAndInitializeGA();
  }, []);

  // Listen for consent updates from the banner
  useEffect(() => {
    const handleConsentUpdate = (event: Event) => {
      console.log("Received cookieConsentUpdated event:", (event as CustomEvent).detail);
      loadAndInitializeGA(); // Re-check consent and load GA if needed
    };

    window.addEventListener('cookieConsentUpdated', handleConsentUpdate);

    return () => {
      window.removeEventListener('cookieConsentUpdated', handleConsentUpdate);
    };
  }, []); // Empty dependency array ensures this runs only once

  // GA Config and Page View Tracking - Runs only when gaInitialized state is true
  useEffect(() => {
    if (gaInitialized && typeof window.gtag === 'function') {
      console.log(`Configuring GA and tracking page_view for: ${location.pathname + location.search}`);
      
      // Configure GA - gtag handles duplicate calls gracefully
      window.gtag('config', GA_MEASUREMENT_ID); 
      
      // Track Page View
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title
      });
    } else if (gaInitialized && typeof window.gtag !== 'function') {
        console.warn("GA was initialized, but window.gtag is not available. Waiting for script?");
        // Potentially add a small delay/retry here if script loading is slow, though onload should handle it.
    }
  }, [gaInitialized, location]); // Depend on initialization status and location changes

  console.log("App component rendering. GA Initialized:", gaInitialized);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PrototypeBadge />
        <CookieConsentBanner /> {/* Render the banner */}
        <>
          <ScrollToTop />
          <Routes>
            {/* Wrap public routes in the layout */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Index />} />
              <Route path="/om-oss" element={<OmOss />} />
              <Route path="/om-demens" element={<OmDemens />} />
              <Route path="/kampanjer" element={<Kampanjer />} />
              <Route path="/kampanjer/freddy-cyklar" element={<FreddyCyklar />} />
              <Route path="/kampanjer/freddy-cyklar/bli-sponsor" element={<BliSponsor />} />
              <Route path="/kontakt" element={<Kontakt />} />
              <Route path="/berattelser" element={<Berattelser />} />
              <Route path="/berattelser/tidiga-tecken-pa-demens" element={<TidigaTeckenPaDemens />} />
              <Route path="/berattelser/kommunicera-med-personer-med-demens" element={<KommuniceraMedPersonerMedDemens />} />
              <Route path="/berattelser/sju-faser-av-demens-och-alzheimer" element={<SjuFaserAvDemensOchAlzheimer />} /> {/* Added route */}
              <Route path="/berattelser/ett-demensvanligt-boende" element={<EttDemensvanligtBoende />} /> {/* Added route */}
              <Route path="/community" element={<Community />} />
              <Route path="/integritetspolicy" element={<Integritetspolicy />} />
              <Route path="/community-riktlinjer" element={<CommunityRiktlinjer />} />
              <Route path="/cookiepolicy" element={<CookiePolicy />} />
              <Route path="/form-confirmation" element={<FormConfirmation />} />
            </Route>

            {/* Catch all route (can be inside or outside layout depending on desired behavior) */}
            {/* Placing it outside for now, so NotFound doesn't have Navbar/Footer */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
